"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import type { ColumnDef } from '@tanstack/react-table'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { Button } from '@open-mercato/ui/primitives/button'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type ShipmentListItem = {
  id: string
  shipmentNumber: string
  status: string
  cargoSegment: string
  marketType: string
  modeConstraint: string
  originLocationId: string
  destinationLocationId: string
  weightTons: string
  pickupEarliest: string
  createdAt: string
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  published: 'bg-blue-100 text-blue-700',
  matching: 'bg-yellow-100 text-yellow-700',
  quoted: 'bg-purple-100 text-purple-700',
  booked: 'bg-green-100 text-green-700',
  executing: 'bg-orange-100 text-orange-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  settled: 'bg-teal-100 text-teal-700',
  cancelled: 'bg-red-100 text-red-700',
}

export default function ShipmentListPage() {
  const t = useT()
  const router = useRouter()
  const [items, setItems] = React.useState<ShipmentListItem[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [statusFilter, setStatusFilter] = React.useState<string>('')

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const filterParam = statusFilter ? `&status=${statusFilter}` : ''
      const response = await readApiResultOrThrow<{ items?: ShipmentListItem[] }>(
        `/api/freight/marketplace/shipments?pageSize=100${filterParam}`,
        undefined,
        { errorMessage: t('marketplace.pages.list.loadError', 'Failed to load shipments.') },
      )
      setItems(Array.isArray(response.items) ? response.items : [])
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('marketplace.pages.list.loadError', 'Failed to load shipments.'))
    } finally {
      setLoading(false)
    }
  }, [t, statusFilter])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const statuses = ['', 'draft', 'published', 'matching', 'quoted', 'booked', 'executing', 'delivered', 'settled', 'cancelled']
  const columns = React.useMemo<ColumnDef<ShipmentListItem>[]>(() => [
    {
      accessorKey: 'shipmentNumber',
      header: t('marketplace.fields.shipmentNumber', 'Number'),
      cell: ({ row }) => (
        <Link href={`/backend/marketplace/${row.original.id}`} className="font-medium text-primary hover:underline">
          {row.original.shipmentNumber}
        </Link>
      ),
      meta: { priority: 1, sticky: true },
    },
    {
      accessorKey: 'status',
      header: t('marketplace.fields.status', 'Status'),
      cell: ({ row }) => (
        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[row.original.status] ?? 'bg-gray-100 text-gray-700'}`}>
          {t(`marketplace.status.${row.original.status}`, row.original.status)}
        </span>
      ),
      meta: { maxWidth: '9rem' },
    },
    {
      accessorKey: 'cargoSegment',
      header: t('marketplace.fields.cargoSegment', 'Cargo'),
      cell: ({ row }) => t(`marketplace.cargoSegment.${row.original.cargoSegment}`, row.original.cargoSegment),
      meta: { maxWidth: '12rem' },
    },
    {
      accessorKey: 'marketType',
      header: t('marketplace.fields.marketType', 'Market'),
      cell: ({ row }) => t(`marketplace.marketType.${row.original.marketType}`, row.original.marketType),
      meta: { maxWidth: '10rem' },
    },
    {
      accessorKey: 'weightTons',
      header: t('marketplace.fields.weightTons', 'Weight (t)'),
      meta: { maxWidth: '8rem' },
    },
    {
      accessorKey: 'pickupEarliest',
      header: t('marketplace.fields.pickupEarliest', 'Pickup'),
      cell: ({ row }) => new Date(row.original.pickupEarliest).toLocaleDateString(),
      meta: { maxWidth: '9rem' },
    },
    {
      accessorKey: 'createdAt',
      header: t('marketplace.fields.createdAt', 'Created'),
      cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
      meta: { maxWidth: '9rem' },
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <DataTable<ShipmentListItem>
          title={(
            <div>
              <div>{t('marketplace.pages.list.title', 'Shipments')}</div>
              <p className="mt-1 text-sm font-normal text-muted-foreground">
                {t('marketplace.pages.list.description', 'Manage freight marketplace shipments and their lifecycle.')}
              </p>
            </div>
          )}
          data={items}
          columns={columns}
          isLoading={loading}
          error={error}
          toolbar={(
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {statuses.map((statusValue) => (
                <option key={statusValue} value={statusValue}>
                  {statusValue ? t(`marketplace.status.${statusValue}`, statusValue) : t('marketplace.pages.list.allStatuses', 'All statuses')}
                </option>
              ))}
            </select>
          )}
          actions={(
            <Button asChild>
              <Link href="/backend/marketplace/create">
                {t('marketplace.pages.create.action', 'Create shipment')}
              </Link>
            </Button>
          )}
          emptyState={(
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('marketplace.pages.list.empty', 'No shipments found.')}
            </p>
          )}
          rowActions={(row) => (
            <RowActions
              items={[
                { id: 'open', label: t('marketplace.actions.open', 'Open'), href: `/backend/marketplace/${row.id}` },
              ]}
            />
          )}
          onRowClick={(row) => router.push(`/backend/marketplace/${row.id}`)}
          injectionSpotId="data-table:freight.marketplace.shipments"
          replacementHandle="data-table:freight.marketplace.shipments"
        />
      </PageBody>
    </Page>
  )
}
